import React, { useState } from 'react';
import {
  ShieldExclamationIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const RISK_COLORS = {
  High:   'bg-red-100 text-red-800',
  Medium: 'bg-amber-100 text-amber-800',
  Low:    'bg-blue-100 text-blue-800',
};

const RISK_BORDER = {
  High:   '#EF4444',
  Medium: '#F59E0B',
  Low:    '#3B82F6',
};

const RISK_SORT = { High: 0, Medium: 1, Low: 2 };

const SEV_COLORS = {
  High:   'bg-red-100 text-red-800',
  Medium: 'bg-amber-100 text-amber-800',
  Low:    'bg-blue-100 text-blue-800',
};

const RiskBadge = ({ risk }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${RISK_COLORS[risk] ?? RISK_COLORS.Low}`}>
    {risk ?? '—'}
  </span>
);

const SevBadge = ({ severity }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SEV_COLORS[severity] ?? SEV_COLORS.Low}`}>
    {severity ?? '—'}
  </span>
);

const CspmRequiredBanner = () => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6">
    <div className="flex items-start gap-3">
      <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold text-amber-900">No attack paths found</p>
        <p className="text-sm text-amber-800 mt-1">
          Attack path analysis requires <strong>Microsoft Defender CSPM</strong> (paid plan) to be enabled on each
          subscription. Subscriptions without Defender CSPM return no attack paths — this is not an error.
        </p>
        <a
          href="https://portal.azure.com/#view/Microsoft_Azure_Security/SecurityMenuBlade/~/4"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-3 text-sm text-amber-700 hover:text-amber-900 font-medium underline"
        >
          Enable Defender CSPM in Azure Portal
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  </div>
);

const AttackPathSummaryStrip = ({ paths }) => {
  const high   = paths.filter(p => (p.properties?.riskLevel ?? 'Low') === 'High').length;
  const medium = paths.filter(p => (p.properties?.riskLevel ?? 'Low') === 'Medium').length;
  const low    = paths.filter(p => (p.properties?.riskLevel ?? 'Low') === 'Low').length;
  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      <div className="bg-white shadow rounded-lg p-5">
        <p className="text-sm text-gray-500">High Risk</p>
        <p className={`text-3xl font-bold mt-1 ${high > 0 ? 'text-red-600' : 'text-gray-900'}`}>{high}</p>
        <p className="text-xs text-gray-400 mt-1">attack paths</p>
      </div>
      <div className="bg-white shadow rounded-lg p-5">
        <p className="text-sm text-gray-500">Medium Risk</p>
        <p className={`text-3xl font-bold mt-1 ${medium > 0 ? 'text-amber-600' : 'text-gray-900'}`}>{medium}</p>
        <p className="text-xs text-gray-400 mt-1">attack paths</p>
      </div>
      <div className="bg-white shadow rounded-lg p-5">
        <p className="text-sm text-gray-500">Low Risk</p>
        <p className="text-3xl font-bold mt-1 text-blue-600">{low}</p>
        <p className="text-xs text-gray-400 mt-1">attack paths</p>
      </div>
    </div>
  );
};

const AttackPathPropagationNotice = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-5 text-sm text-blue-900">
    <strong>What are attack paths?</strong> Azure Defender CSPM maps potential attack routes through your environment —
    chains of misconfigurations and exposures that an attacker could exploit together. Fixing the linked recommendations
    below breaks the chain and removes the path.
  </div>
);

const AttackPathCard = ({ path, onSelectRec }) => {
  const [expanded, setExpanded] = useState(false);
  const p    = path.properties ?? {};
  const risk = p.riskLevel ?? 'Low';
  const name = p.displayName ?? path.name ?? 'Unknown Attack Path';
  const linked = path.linkedRecommendations ?? [];

  return (
    <div
      className="bg-white shadow rounded-lg overflow-hidden mb-3"
      style={{ borderLeft: `4px solid ${RISK_BORDER[risk] ?? RISK_BORDER.Low}` }}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        {expanded
          ? <ChevronDownIcon  className="h-4 w-4 text-gray-400 flex-shrink-0" />
          : <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-gray-900">{name}</p>
            <RiskBadge risk={risk} />
          </div>
          {p.description && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{p.description}</p>
          )}
        </div>
        {linked.length > 0 && (
          <span className="flex-shrink-0 text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5">
            {linked.length} rec{linked.length !== 1 ? 's' : ''}
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 bg-gray-50 space-y-4">
          {p.description && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</p>
              <p className="text-sm text-gray-700">{p.description}</p>
            </div>
          )}
          {p.potentialImpact && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Potential Impact</p>
              <p className="text-sm text-gray-700">{p.potentialImpact}</p>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Linked Recommendations {linked.length > 0 && `(${linked.length})`}
            </p>
            {linked.length === 0 ? (
              <p className="text-sm text-gray-400">No linked recommendations found for this attack path.</p>
            ) : (
              <div className="space-y-1.5">
                {linked.map(rec => (
                  <button
                    key={rec.assessmentKey}
                    onClick={() => onSelectRec(rec)}
                    className="w-full flex items-center gap-3 px-3 py-2 bg-white border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-200 transition-colors text-left group"
                  >
                    <ShieldExclamationIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                    <span className="flex-1 text-sm text-gray-800 group-hover:text-blue-700 truncate">{rec.displayName}</span>
                    <SevBadge severity={rec.severity} />
                  </button>
                ))}
              </div>
            )}
          </div>
          {path._subName && (
            <p className="text-xs text-gray-400">Subscription: {path._subName}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function AttackPathsTab({ visibleSubscriptions, subData, allRecommendations, onSelectRec }) {
  const rawPaths = [];
  for (const sub of visibleSubscriptions) {
    const d = subData[sub.subscriptionId];
    if (d && !d.loading && !d.error) {
      for (const path of d.attackPaths ?? []) {
        rawPaths.push({ ...path, _subId: sub.subscriptionId, _subName: sub.displayName ?? sub.subscriptionId });
      }
    }
  }

  const recByKey = new Map(allRecommendations.map(r => [r.assessmentKey, r]));

  const enrichedPaths = rawPaths
    .map(path => ({
      ...path,
      linkedRecommendations: (path.properties?.assessmentKeys ?? []).map(k => recByKey.get(k)).filter(Boolean),
    }))
    .sort((a, b) => {
      const ra = RISK_SORT[a.properties?.riskLevel] ?? 99;
      const rb = RISK_SORT[b.properties?.riskLevel] ?? 99;
      return ra - rb;
    });

  if (enrichedPaths.length === 0) {
    return <CspmRequiredBanner />;
  }

  return (
    <>
      <AttackPathSummaryStrip paths={enrichedPaths} />
      <AttackPathPropagationNotice />
      <div>
        {enrichedPaths.map(path => (
          <AttackPathCard
            key={`${path._subId}-${path.id ?? path.name}`}
            path={path}
            onSelectRec={onSelectRec}
          />
        ))}
      </div>
    </>
  );
}
